<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="!isProcessing"
    :show-close="!isProcessing"
    :title="`${title}导入`"
    width="760px"
    top="10vh"
    @closed="reset"
  >
    <div class="import-file-notice">
      <i class="el-icon-warning-outline" />
      导入期间如修改 Excel 文件，请保存后重新选择文件，再点击“确定导入”。
    </div>

    <div class="upload-section">
      <el-upload
        ref="upload"
        class="import-dropzone"
        action=""
        drag
        :disabled="isProcessing"
        :auto-upload="false"
        :limit="1"
        :file-list="fileList"
        :show-file-list="false"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        :on-exceed="handleFileExceed"
        accept=".xlsx"
      >
        <div class="dropzone-content">
          <span class="upload-icon"><i class="el-icon-upload2" /></span>
          <div class="upload-title">点击选择文件，或将文件拖拽至此处</div>
          <div class="upload-description">仅支持一个 .xlsx 文件，重新选择文件会自动替换</div>
        </div>
      </el-upload>

      <div v-if="selectedFile" class="selected-file">
        <div class="selected-file-name">
          <i class="el-icon-document" />
          <span>{{ selectedFile.name }}</span>
        </div>
        <el-button type="text" :disabled="isProcessing" @click="clearSelectedFile">移除文件</el-button>
      </div>
      <div v-else class="upload-hint">模板中的“导入说明”工作表包含字段、必填项和填写规则。</div>
    </div>

    <div v-if="previewResult" class="result-summary">
      <el-alert
        v-if="!previewResult.valid"
        type="error"
        :closable="false"
        show-icon
        :title="`校验失败，共发现 ${previewResult.errors.length} 项问题。请修改文件后重新上传校验。`"
      />
      <el-alert
        v-else
        type="success"
        :closable="false"
        show-icon
        :title="`文件校验通过：共 ${previewResult.totalRows} 条，新增 ${previewResult.newCount} 条，重复 ${previewResult.duplicateCount} 条。`"
      />
    </div>

    <el-table v-if="previewResult && previewResult.errors.length" :data="previewResult.errors" border size="mini" max-height="260">
      <el-table-column prop="rowNo" label="Excel 行号" width="95" />
      <el-table-column prop="field" label="字段" width="125" />
      <el-table-column prop="message" label="错误原因" min-width="250" />
      <el-table-column prop="value" label="原始内容" min-width="180" show-overflow-tooltip />
    </el-table>

    <template v-if="previewResult && previewResult.valid && previewResult.duplicates.length">
      <div class="duplicate-title">数据库重复项</div>
      <el-table :data="previewResult.duplicates" border size="mini" max-height="260">
        <el-table-column prop="rowNo" label="Excel 行号" width="95" />
        <el-table-column prop="key" label="重复键" min-width="250" show-overflow-tooltip />
        <el-table-column prop="existingId" label="已有记录ID" width="110" />
        <el-table-column prop="existingDisplay" label="已有记录" min-width="220" show-overflow-tooltip />
      </el-table>
    </template>

    <div slot="footer" class="import-footer">
      <el-button
        icon="el-icon-download"
        :disabled="isProcessing"
        :loading="templateLoading"
        @click="downloadTemplate"
      >下载导入模板</el-button>
      <div class="footer-actions">
        <el-button :disabled="isProcessing" @click="dialogVisible = false">取消导入</el-button>
        <template v-if="hasDuplicates">
          <el-button
            type="warning"
            :disabled="isProcessing"
            :loading="executing"
            @click="execute('OVERWRITE')"
          >覆盖全部重复数据并导入</el-button>
          <el-button
            type="primary"
            :disabled="isProcessing"
            :loading="executing"
            @click="execute('SKIP')"
          >跳过重复数据并导入</el-button>
        </template>
        <template v-else>
          <el-button
            type="primary"
            :disabled="!selectedFile || isProcessing"
            :loading="isProcessing"
            @click="startImport"
          >{{ primaryButtonLabel }}</el-button>
        </template>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    templateLoader: { type: Function, required: true },
    previewLoader: { type: Function, required: true },
    executeLoader: { type: Function, required: true }
  },
  data() {
    return {
      selectedFile: null,
      fileList: [],
      templateLoading: false,
      previewLoading: false,
      executing: false,
      previewResult: null
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    hasDuplicates() {
      return !!(this.previewResult && this.previewResult.valid && this.previewResult.duplicateCount > 0)
    },
    isProcessing() {
      return this.previewLoading || this.executing
    },
    primaryButtonLabel() {
      if (this.previewLoading) return '正在校验数据…'
      if (this.executing) return '正在导入…'
      return '确定导入'
    }
  },
  methods: {
    downloadTemplate() {
      this.templateLoading = true
      Promise.resolve(this.templateLoader())
        .finally(() => {
          this.templateLoading = false
        })
    },
    handleFileChange(file) {
      this.selectedFile = file.raw
      this.fileList = [file]
      this.resetPreview()
    },
    handleFileRemove() {
      this.selectedFile = null
      this.fileList = []
      this.resetPreview()
    },
    clearSelectedFile() {
      this.$refs.upload.clearFiles()
      this.handleFileRemove()
    },
    handleFileExceed(files) {
      this.$refs.upload.clearFiles()
      this.$nextTick(() => {
        this.$refs.upload.handleStart(files[0])
      })
    },
    async startImport() {
      if (!this.selectedFile) return
      this.previewLoading = true
      try {
        const result = await this.previewLoader(this.selectedFile)
        this.previewResult = result
        if (!result.valid || result.duplicateCount > 0) return
        await this.execute('SKIP')
      } finally {
        this.previewLoading = false
      }
    },
    execute(strategy) {
      if (!this.selectedFile || !this.previewResult || !this.previewResult.valid) return
      this.executing = true
      return Promise.resolve(this.executeLoader(this.selectedFile, strategy))
        .then(result => {
          this.$message.success(`导入完成：新增 ${result.createdCount} 条，覆盖 ${result.updatedCount} 条，跳过 ${result.skippedCount} 条`)
          this.$emit('success', result)
          this.dialogVisible = false
        })
        .finally(() => {
          this.executing = false
        })
    },
    resetPreview() {
      this.previewResult = null
    },
    reset() {
      this.selectedFile = null
      this.fileList = []
      this.previewResult = null
      this.previewLoading = false
      this.executing = false
    }
  }
}
</script>

<style scoped>
.import-file-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid #fde68a;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
  line-height: 20px;
}

.import-file-notice i {
  flex: 0 0 auto;
  color: #d97706;
  font-size: 16px;
}

.upload-section {
  margin-bottom: 18px;
}

.import-dropzone {
  width: 100%;
}

.import-dropzone ::v-deep .el-upload,
.import-dropzone ::v-deep .el-upload-dragger {
  width: 100%;
}

.import-dropzone ::v-deep .el-upload-dragger {
  height: 176px;
  border: 1px dashed #93c5fd;
  border-radius: 14px;
  background: linear-gradient(180deg, #f8fbff 0%, #f0f7ff 100%);
  transition: all .2s ease;
}

.import-dropzone ::v-deep .el-upload-dragger:hover {
  border-color: #3b82f6;
  background: #eef6ff;
  box-shadow: 0 8px 24px rgba(59, 130, 246, .10);
}

.dropzone-content {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.upload-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  margin-bottom: 12px;
  border-radius: 50%;
  color: #fff;
  font-size: 26px;
  background: linear-gradient(135deg, #60a5fa, #2563eb);
  box-shadow: 0 8px 18px rgba(37, 99, 235, .24);
}

.upload-title {
  color: #1e3a5f;
  font-size: 16px;
  font-weight: 600;
}

.upload-description,
.upload-hint {
  margin-top: 8px;
  color: #7b8798;
  font-size: 13px;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
}

.selected-file-name {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  color: #334155;
}

.selected-file-name i {
  color: #3b82f6;
  font-size: 18px;
}

.selected-file-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-summary {
  margin-bottom: 14px;
}

.duplicate-title {
  margin: 16px 0 8px;
  color: #303847;
  font-weight: 600;
}

.import-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-actions {
  display: flex;
  gap: 10px;
}
</style>
